import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useAuth from './useAuth';
import { cartActions } from '../redux/slices/cartSlice';
import { wishlistActions } from '../redux/slices/wishlistSlice';
import { fetchUserData, syncUserData } from '../services/userSyncService';

export const useUserSync = () => {
  const { currentUser, loading } = useAuth();
  const dispatch = useDispatch();

  const cartItems = useSelector((state) => state.cart.cartItems);
  const wishlistItems = useSelector((state) => state.wishlist.wishlistItems);

  const prevUserRef = useRef(null);
  const isInitializedRef = useRef(false);
  const syncTimeoutRef = useRef(null);

  // 1. Handle Login & Initial Fetch from Firestore
  useEffect(() => {
    if (loading) return;

    if (currentUser) {
      // If new user logged in
      if (prevUserRef.current !== currentUser.uid) {
        prevUserRef.current = currentUser.uid;
        isInitializedRef.current = false;

        const loadRemote = async () => {
          try {
            const { cart, wishlist } = await fetchUserData(currentUser.uid);
            // Load remote cart & wishlist into Redux
            dispatch(cartActions.setCart(cart));
            dispatch(wishlistActions.setWishlist(wishlist));
          } catch (err) {
            console.error('Error fetching remote cart/wishlist:', err);
          } finally {
            isInitializedRef.current = true;
          }
        };

        loadRemote();
      }
    } else {
      // User is logged out or unauthenticated guest
      if (prevUserRef.current !== null || cartItems.length > 0 || wishlistItems.length > 0) {
        prevUserRef.current = null;
        isInitializedRef.current = false;

        // Clear in-memory & local state on logout
        dispatch(cartActions.clearCart());
        dispatch(wishlistActions.clearWishlist());
        localStorage.removeItem('cartItems');
        localStorage.removeItem('wishlistItems');
        localStorage.removeItem('cachedAuthProfile');
      }
    }
  }, [currentUser, loading, dispatch, cartItems.length, wishlistItems.length]);

  // 2. Sync changes back to Firestore (Debounced 500ms)
  useEffect(() => {
    if (!currentUser || !isInitializedRef.current) return;

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      syncUserData(currentUser.uid, {
        cart: cartItems,
        wishlist: wishlistItems,
      });
    }, 500);

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [currentUser, cartItems, wishlistItems]);
};

export default useUserSync;
